/**
 * Stored notifications (notification-service, /v1/notifications).
 * The bell and the Today view used to call /v1/notify, which doesn't exist, so the list
 * was always empty and "mark as read" never reached the server.
 */
import i18next from 'i18next';
import { api } from './api';

export interface StoredNotification {
  id: string;
  title: string;
  body: string;
  type: string;
  targetId?: string;
  messageId?: string;
  read: boolean;
  createdAt: string;
}

interface NotificationRow {
  id: string;
  type?: string | null;
  title?: string | null;
  body?: string | null;
  data?: Record<string, unknown> | null;
  read_at?: string | null;
  created_at?: string;
}

/** Same targetId/messageId rules as live notifications (useNotificationSocket). */
export function toStoredNotification(row: NotificationRow): StoredNotification {
  const data = row.data ?? {};
  const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined);
  return {
    id: row.id,
    title: row.title ?? '',
    body: row.body ?? '',
    type: row.type ?? 'message',
    targetId: str(data['roomId']) ?? str(data['fileId']),
    messageId: str(data['messageId']),
    read: Boolean(row.read_at),
    createdAt: row.created_at ?? new Date().toISOString(),
  };
}

export async function fetchNotifications(limit = 50): Promise<StoredNotification[] | null> {
  const res = await api.get<{ notifications: NotificationRow[] }>(
    `/v1/notifications?limit=${limit}&lang=${encodeURIComponent(i18next.language || 'en')}`,  // titles in the UI language
  );
  const rows = res.data?.notifications;
  return Array.isArray(rows) ? rows.map(toStoredNotification) : null;
}

export function markNotificationRead(id: string): Promise<unknown> {
  return api.patch(`/v1/notifications/${encodeURIComponent(id)}/read`, {}).catch(() => undefined);
}
